import React, {useState} from 'react';
import {ChevronLeft, ChevronRight, X, Camera, Image as ImageIcon, ChevronDown, ChevronUp} from 'lucide-react';

interface StoreImageGalleryProps {
  images?: string[];
  profileImageUrl?: string | null;
  storeName: string;
}

const StoreImageGallery: React.FC<StoreImageGalleryProps> = ({images, profileImageUrl, storeName}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // دمج جميع الصور المتاحة - الصورة الرئيسية أولاً ثم الصور الإضافية
  const allImages = [];
  if (profileImageUrl) {
    allImages.push(profileImageUrl);
  }
  if (images && images.length > 0) {
    allImages.push(...images);
  }

  const displayImages = allImages;

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevious = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex > 0 ? selectedImageIndex - 1 : displayImages.length - 1);
    }
  };

  const goToNext = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex < displayImages.length - 1 ? selectedImageIndex + 1 : 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {closeLightbox();}
    if (e.key === 'ArrowLeft') {goToPrevious();}
    if (e.key === 'ArrowRight') {goToNext();}
  };

  if (displayImages.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">لا توجد صور متاحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center justify-between gap-2 hover:text-brand-600 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          صور المتجر ({displayImages.length})
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronUp className="w-4 h-4" />
        )}
      </button>

      {/* المعرض المصغر */}
      {!isCollapsed ? (
        <div className="grid grid-cols-2 gap-2">
          {displayImages.slice(0, 4).map((image, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group bg-gray-100"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image}
                alt={`${storeName} - صورة ${index + 1}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />

              {/* تأثير hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              {/* عداد الصور في الصورة الأخيرة */}
              {index === 3 && displayImages.length > 4 && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    +{displayImages.length - 4}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* صورة مصغرة عندما يكون المعرض مطوياً */
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div
            className="w-16 h-16 rounded-lg overflow-hidden cursor-pointer group bg-gray-100"
            onClick={() => openLightbox(0)}
          >
            <img
              src={displayImages[0]}
              alt={`${storeName} - صورة 1`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600">انقر للعرض الكامل</p>
            <p className="text-xs text-gray-500">{displayImages.length} صورة متاحة</p>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* زر الإغلاق */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* أزرار التنقل */}
          {displayImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* الصورة الرئيسية */}
          <div className="max-w-4xl max-h-[80vh] mx-4">
            <img
              src={displayImages[selectedImageIndex]}
              alt={`${storeName} - صورة ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>

          {/* مؤشر الصور */}
          {displayImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
              {displayImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === selectedImageIndex
                      ? 'bg-white'
                      : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                  }`}
                />
              ))}
            </div>
          )}

          {/* عداد الصور */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
            {selectedImageIndex + 1} من {displayImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreImageGallery;
